import type { DemoAccessAssignment } from "@/store/api/baseApi";
import {
  VNextButton,
  VNextCard,
  VNextNotice,
  VNextPill,
} from "@/vnext/primitives";
import {
  buildDemoAccessRevokeAriaLabel,
  buildDemoAccessRevokeTarget,
  type DemoAccessRevokeTarget,
} from "@/vnext/routes/settings/demo-access/demoAccessRouteModel";
import { formatVNextDateTime } from "@/vnext/shared/formatters";

interface DemoAccessAssignmentsCardProps {
  assignments: DemoAccessAssignment[];
  errorMessage: string | null;
  inactiveAssignmentsCount: number;
  isLoading: boolean;
  isRevoking: boolean;
  onOpenRevokeDialog: (target: DemoAccessRevokeTarget) => void;
  onRetry: () => void;
  onToggleShowDisabledAssignments: () => void;
  showDisabledAssignments: boolean;
}

export function DemoAccessAssignmentsCard({
  assignments,
  errorMessage,
  inactiveAssignmentsCount,
  isLoading,
  isRevoking,
  onOpenRevokeDialog,
  onRetry,
  onToggleShowDisabledAssignments,
  showDisabledAssignments,
}: DemoAccessAssignmentsCardProps) {
  const hasDisabledHistory = inactiveAssignmentsCount > 0;
  const subtitle = showDisabledAssignments
    ? "Showing active assignments plus disabled history so you can audit previous grants."
    : "Showing users who can currently enter the demo company. Open disabled history only when you need to audit past grants.";

  return (
    <VNextCard
      actions={
        hasDisabledHistory ? (
          <VNextButton onClick={onToggleShowDisabledAssignments} tone="quiet">
            {showDisabledAssignments
              ? "Hide disabled history"
              : `Show disabled history (${inactiveAssignmentsCount})`}
          </VNextButton>
        ) : undefined
      }
      subtitle={subtitle}
      title="Current assignments"
    >
      {isLoading ? (
        <div className="vnext-empty">Loading demo access assignments…</div>
      ) : errorMessage ? (
        <VNextNotice
          action={
            <VNextButton onClick={onRetry} tone="quiet">
              Retry
            </VNextButton>
          }
          tone="danger"
        >
          {errorMessage}
        </VNextNotice>
      ) : assignments.length === 0 ? (
        <div className="vnext-empty">
          {showDisabledAssignments
            ? "No demo access assignments found."
            : hasDisabledHistory
              ? "No users currently have demo access. Show disabled history to review previous assignments."
              : "No users currently have demo access."}
        </div>
      ) : (
        <div className="demo-access-route__assignment-list">
          {assignments.map((assignment) => {
            const revokeTarget = buildDemoAccessRevokeTarget(assignment);

            return (
              <article
                className="demo-access-route__assignment"
                key={assignment.firebaseId}
              >
                <div className="demo-access-route__assignment-main">
                  <div className="demo-access-route__assignment-header">
                    <div>
                      <h3 className="demo-access-route__assignment-title">
                        {assignment.displayName}
                      </h3>
                      <p className="demo-access-route__assignment-email">
                        {assignment.email}
                      </p>
                    </div>
                    <VNextPill
                      tone={
                        assignment.demoAccessEnabled ? "success" : "warning"
                      }
                    >
                      {assignment.demoAccessEnabled ? "Enabled" : "Disabled"}
                    </VNextPill>
                  </div>

                  <dl className="demo-access-route__assignment-meta">
                    <div>
                      <dt>Firebase UID</dt>
                      <dd>{assignment.firebaseId}</dd>
                    </div>
                    <div>
                      <dt>Granted by</dt>
                      <dd>{assignment.grantedBy ?? "Not recorded"}</dd>
                    </div>
                    <div>
                      <dt>Last updated</dt>
                      <dd>{formatVNextDateTime(assignment.updatedAt)}</dd>
                    </div>
                  </dl>
                </div>

                <div className="demo-access-route__assignment-actions">
                  {assignment.demoAccessEnabled ? (
                    <VNextButton
                      aria-label={buildDemoAccessRevokeAriaLabel(revokeTarget)}
                      disabled={isRevoking}
                      onClick={() => onOpenRevokeDialog(revokeTarget)}
                      tone="secondary"
                    >
                      Revoke access
                    </VNextButton>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </VNextCard>
  );
}
